import { TableDataView } from 'vl-common/src/components/Table/TableDataView';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import AuditDetail from './AuditDetail';
import { compareAsc, isAfter, startOfDay } from 'date-fns';
import { Api } from 'vl-common/src/api';
import { AuditSummaryParams } from 'vl-common/src/api/api.audit';
import Pagination from 'vl-common/src/components/Table/Pagination';
import {
  allAuditCategories,
  AuditedCategory,
  defaultEndDate,
  defaultStartDate,
  getItemKey,
  isOutOfAllowedPeriod
} from './auditData';
import { memoize } from 'lodash';

type NormalisedAuditData = {
  category: AuditedCategory;
  key: string;
  created_date: Date;
  created_by: string;
};

function resolveCacheKey(...args) {
  return JSON.stringify(args);
}
const AuditApis: { [key in AuditedCategory]: (params: AuditSummaryParams) => any } = {
  cases: memoize(Api.audit.getCasesAuditSummary, resolveCacheKey),
  appointments: memoize(Api.audit.getAppointmentsAuditSummary, resolveCacheKey),
  tasks: memoize(Api.audit.getTasksAuditSummary, resolveCacheKey),
  outcomes: memoize(Api.audit.getAppointmentsOutcomesSummary, resolveCacheKey)
};

function normaliseAuditData(category, rawAuditData: any): NormalisedAuditData[] {
  return rawAuditData.audit_data.map((item) => ({
    category: category,
    key: getItemKey(rawAuditData.audit_keys[0], item),
    created_date: new Date(item.created_date + 'Z'),
    created_by: item.created_by
  }));
}

async function getAggregateAudit({
  categories = new Set([]),
  end_date,
  dateFrom,
  pat_guid = undefined,
  rows = 10
}: {
  categories?: Set<AuditedCategory>;
  start_date: string;
  end_date: string;
  dateFrom: string;
  pat_guid?: string;
  rows?: number;
}): Promise<{ data: NormalisedAuditData[]; dateFrom: string | undefined }> {
  if (!dateFrom || isAfter(startOfDay(new Date(dateFrom)), startOfDay(new Date(end_date)))) {
    return { data: [], dateFrom: undefined };
  }

  const promises: Promise<any>[] = [];
  const categoriesOrder = [];

  for (const category of categories) {
    promises.push(
      AuditApis[category]({
        start_date: dateFrom,
        end_date: end_date,
        pat_guid,
        rows
      })
    );
    categoriesOrder.push(category);
  }

  const rowAuditData = (await Promise.allSettled(promises)).map((data, index) => {
    if (data.status === 'fulfilled') {
      return normaliseAuditData(categoriesOrder[index], data.value.data);
    }
    return [];
  });

  const auditData = rowAuditData
    .flat()
    .sort(({ created_date: ld }, { created_date: rd }) => compareAsc(new Date(ld), new Date(rd)));

  const nextPageDate = auditData[rows]?.created_date;
  const isNextPageDateValid =
    nextPageDate && !isAfter(startOfDay(new Date(nextPageDate)), startOfDay(new Date(end_date)));

  return {
    data: auditData.slice(0, rows),
    dateFrom: isNextPageDateValid ? nextPageDate.toISOString() : undefined
  };
}

function useAuditSummary(
  start_date: string,
  end_date: string,
  query,
  setQuery,
  allowedCategories?: Set<AuditedCategory>,
  pat_guid: string = undefined
) {
  const [rows, setRows] = useState<any>([]);
  const [tableKey, setTableKey] = useState(0);

  const [pageNumber, setPageNumber] = useState(1);
  const [isLastPage, setIsLastPage] = useState(false);

  const [paginationCache, setPaginationCache] = useState([]);
  const curDate = useRef('');

  const categoriesToUse = useMemo(() => {
    return query.categories && query.categories.length > 0 ? new Set(query.categories) : allowedCategories;
  }, [query.categories, allowedCategories]);

  useEffect(() => {
    setQuery({
      start_date,
      end_date,
      // @ts-expect-error
      categories: [...allowedCategories]
    });
    curDate.current = start_date;
  }, [allowedCategories, setQuery, start_date, end_date]);

  useEffect(() => {
    setPageNumber(1);
    if (isOutOfAllowedPeriod(query.start_date, query.end_date)) {
      setRows([]);
    } else {
      setTableKey((tk) => tk + 1);
      curDate.current = query.start_date;
      getAggregateAudit({
        ...query,
        categories: categoriesToUse,
        pat_guid,
        start_date: query.start_date || start_date,
        end_date: query.end_date || end_date,
        dateFrom: curDate.current || start_date
      }).then((res) => {
        setIsLastPage(!res.dateFrom);
        setRows(res.data || []);
        setPaginationCache([query.start_date, res.dateFrom]);
      });
    }
  }, [allowedCategories, query, pat_guid, start_date, end_date, categoriesToUse]);

  useEffect(() => {
    if (isOutOfAllowedPeriod(query.start_date, query.end_date)) {
      setRows([]);
    } else {
      curDate.current = paginationCache[pageNumber - 1];
      getAggregateAudit({
        ...query,
        categories: categoriesToUse,
        pat_guid,
        start_date: query.start_date || start_date,
        end_date: query.end_date || end_date,
        dateFrom: curDate.current || start_date
      }).then((res) => {
        setRows(res.data || []);
        if (pageNumber >= paginationCache.length && pageNumber > 1) {
          setPaginationCache((cache) => [...cache, res.dateFrom]);
        }
        setIsLastPage(!res.dateFrom);
      });
    }
  }, [end_date, pageNumber, paginationCache, pat_guid, query, start_date, categoriesToUse]);

  return {
    tableKey,
    rows,
    pageNumber,
    setPageNumber,
    isLastPage,
    isOutOfAllowedPeriod: isOutOfAllowedPeriod(query.start_date, query.end_date)
  };
}

interface AuditSummaryProps {
  query: any;
  setQuery: any;
  handleTableChange: any;
  tableColumns: any;
  allowedCategories?: Set<AuditedCategory>;
  user_guid?: string;
  start_date?: string;
  end_date?: string;
}

const AuditSummary = ({
  query,
  setQuery,
  handleTableChange,
  tableColumns,
  allowedCategories = allAuditCategories,
  user_guid = undefined,
  start_date = defaultStartDate,
  end_date = defaultEndDate
}: AuditSummaryProps) => {
  const { tableKey, rows, pageNumber, setPageNumber, isLastPage, isOutOfAllowedPeriod } = useAuditSummary(
    start_date,
    end_date,
    query,
    setQuery,
    allowedCategories,
    user_guid
  );

  return (
    <>
      <TableDataView
        tableLayout="fixed"
        handleTableChange={handleTableChange}
        keyValue={tableKey}
        noDataFoundText={isOutOfAllowedPeriod ? 'Period should be less than 14 days' : 'No Items Found'}
        headerItems={tableColumns}
        dataSource={rows}
        rowKey="key"
        expandedRowRender={(data) => (
          <AuditDetail id={data.key} category={data.category} start_date={start_date} end_date={end_date} />
        )}
      />
      <div style={{ paddingBottom: '0.5rem' }}>
        <Pagination
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          isLastPage={isLastPage}
          loading={false}
          setLoading={() => {}}
        />
      </div>
    </>
  );
};

export default AuditSummary;
